import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckOutlined } from '@ant-design/icons';
import { Button, Modal, Spin, message } from 'antd';
import cn from 'classnames';

import {
    useGetAccountLimitsQuery,
    useSelectAccountMutation,
} from '../../api/plan-migration.api';
import type { AccountInfo } from '../../model/types/plan-migration';

import cls from './account-selection-popup.module.css';

interface AccountSelectionPopupProps {
    isOpen: boolean;
    onClose: () => void;
}

export const AccountSelectionPopup = memo(
    (props: AccountSelectionPopupProps) => {
        const { isOpen, onClose } = props;
        const { t } = useTranslation('subscription');

        const { data, isLoading } = useGetAccountLimitsQuery(undefined, {
            skip: !isOpen,
        });
        const [selectAccount, { isLoading: isSelecting }] =
            useSelectAccountMutation();

        const [selectedId, setSelectedId] = useState<number | null>(null);
        const [selectedType, setSelectedType] = useState<string | null>(null);

        const handleSelect = useCallback((account: AccountInfo) => {
            setSelectedId(account.id);
            setSelectedType(account.type);
        }, []);

        const handleConfirm = useCallback(async () => {
            if (!selectedId || !selectedType) {
                return;
            }

            try {
                await selectAccount({
                    account_id: selectedId,
                    account_type: selectedType,
                }).unwrap();

                onClose();
            } catch {
                message.error(t('accountSelection.error'));
            }
        }, [onClose, selectAccount, selectedId, selectedType, t]);

        const platformLabel = (type: string) => {
            const labels: Record<string, string> = {
                mt5: 'MetaTrader 5',
                mt4: 'MetaTrader 4',
                ctrader: 'cTrader',
            };

            return labels[type] ?? type;
        };

        return (
            <Modal
                centered
                className={cls.modal}
                closable={false}
                footer={null}
                maskClosable={false}
                open={isOpen}
                width={480}
            >
                <div className={cls.header}>
                    <h2 className={cls.title}>{t('accountSelection.title')}</h2>
                    <p className={cls.subtitle}>
                        {t('accountSelection.subtitle')}
                    </p>
                </div>

                {isLoading ? (
                    <div className={cls.loader}>
                        <Spin />
                    </div>
                ) : (
                    <>
                        <div className={cls.accountsList}>
                            {data?.accounts.map((account) => {
                                const isSelected =
                                    selectedId === account.id &&
                                    selectedType === account.type;

                                return (
                                    <button
                                        key={`${account.type}-${account.id}`}
                                        className={cn(cls.accountCard, {
                                            [cls.selected]: isSelected,
                                        })}
                                        onClick={() => handleSelect(account)}
                                        type="button"
                                    >
                                        <div className={cls.accountInfo}>
                                            <span className={cls.accountLogin}>
                                                {account.login}
                                            </span>
                                            <span className={cls.accountMeta}>
                                                {platformLabel(account.type)}
                                                {account.broker &&
                                                    ` — ${account.broker}`}
                                            </span>
                                        </div>
                                        <div
                                            className={cn(cls.radioCircle, {
                                                [cls.radioSelected]: isSelected,
                                            })}
                                        >
                                            {isSelected && <CheckOutlined />}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <Button
                            block
                            className={cls.confirmButton}
                            disabled={!selectedId}
                            loading={isSelecting}
                            onClick={handleConfirm}
                            size="large"
                            type="primary"
                        >
                            {t('accountSelection.confirm')}
                        </Button>
                    </>
                )}
            </Modal>
        );
    },
);

AccountSelectionPopup.displayName = 'AccountSelectionPopup';
